import { memo, useMemo, useState } from "react";
import { Link, useLocation } from "wouter";
import { Menu, ChevronDown, ArrowRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { GlobalSearch } from "@/components/shared/GlobalSearch";
import { cn, siteInitials } from "@/lib/utils";
import { useUserAuth } from "@/hooks/use-user-auth";
import { LanguageSwitcher } from "@/components/shared/LanguageSwitcher";
import { SITE_LOGO_IMAGE_WIDTH, sizedUploadUrl } from "@/lib/imageUrl";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { User as UserIcon, LayoutDashboard, Star, AlertTriangle, MessageSquare, LogOut } from "lucide-react";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

type NavMenuItem = {
  id: string;
  title: string;
  href: string;
  visible?: boolean;
  newTab?: boolean;
  children?: NavMenuItem[];
};

/**
 * The site header.
 *
 * `memo` IS THE FIX FOR THE NAV DROPDOWN THAT CLOSES BY ITSELF — read this before
 * removing it.
 *
 * `Navbar` is rendered by `Layout`, and `Layout` is rendered by *every page*. A page
 * that re-renders on a timer therefore re-renders `Layout` and then `Navbar` with it,
 * once a second for as long as it is on screen. `/analysis` and `/news` both do
 * exactly that (they tick a market clock every 1000 ms).
 *
 * A re-render of `Navbar` used to be enough to shut an open nav panel: `DynamicTopNav`
 * and `FallbackTopNav` are defined *inside* this component, so each render produced a
 * new function object, and React compares element **type** by reference. A new type at
 * the same position means "a different component" — React unmounts the old subtree and
 * mounts a fresh one, which throws away the `NavigationMenu`'s internal open state.
 * Hovering a nav item opened the panel; the next clock tick closed it, roughly a second
 * later, usually while the pointer was on its way to a child link.
 *
 * Two guards, both needed:
 *   1. `memo` here — the component takes no props, so a parent re-render no longer
 *      reaches it at all. (Context changes still do: `memo` does not block them.)
 *   2. `navMenu` below — a `useMemo`'d element, so even a re-render of `Navbar` itself
 *      (its own state, `useUserAuth`, or `/api/navigation` landing) hands React the
 *      *same* element reference and React bails out of that subtree instead of
 *      rebuilding it.
 */
function NavbarInner() {
  const [, setLocation] = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Drawer account panel. Collapsed by default, and reset on every close so it is
  // never found already-expanded the next time the drawer opens.
  const [accountOpen, setAccountOpen] = useState(false);
  const { user, isAuthenticated, logout, isLoggingOut } = useUserAuth();
  /**
   * The avatar is now the whole account control, so a picture that fails to load is an
   * empty circle where the printed name used to be a fallback. Storing the URL that
   * failed (rather than a boolean) means a newly uploaded picture is still attempted
   * after an older one 404s.
   */
  const [avatarFailedUrl, setAvatarFailedUrl] = useState<string | null>(null);
  const avatarUrl = user?.profileImageUrl || "";
  const showAvatar = !!avatarUrl && avatarFailedUrl !== avatarUrl;
  /**
   * Same shape and same reason as `avatarFailedUrl`: the *URL* that failed, not a
   * boolean, so a newly uploaded logo is still attempted after an older one 404s.
   *
   * This exists because the mark below is the only thing standing between a missing
   * logo file and Chrome's broken-image glyph in the header — and it used to read
   * `FX`, the previous site's branding. It now falls back to the site's own initials.
   */
  const [logoFailedUrl, setLogoFailedUrl] = useState<string | null>(null);
  const { data: seoCfg } = useQuery<any>({
    queryKey: ["/api/seo"],
    queryFn: async () => {
      const res = await fetch("/api/seo");
      if (!res.ok) return {};
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  const siteName = (seoCfg?.siteName || "BullBuzz").toString();
  const logoUrl = typeof seoCfg?.logoUrl === "string" ? seoCfg.logoUrl : "";
  const tagline = (seoCfg?.tagline || "Trading & Crypto Portal").toString();
  const hasLogo = !!logoUrl.trim();
  /** A configured logo that actually loaded. A 404 falls through to the initials badge. */
  const showLogo = hasLogo && logoFailedUrl !== logoUrl;
  const parts = siteName.trim().split(/\s+/).filter(Boolean);
  const brandA = parts.length > 1 ? parts.slice(0, -1).join(" ") : siteName;
  const brandB = parts.length > 1 ? parts[parts.length - 1] : "";

  const { data: navCfg } = useQuery<{ limit: number | null; items: NavMenuItem[] }>({
    queryKey: ["/api/navigation"],
    queryFn: async () => {
      const res = await fetch("/api/navigation");
      if (!res.ok) return { limit: null, items: [] };
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  const isExternal = (href: string) => /^(https?:)?\/\//i.test(href);
  const hasCustomNav = Array.isArray(navCfg?.items) && navCfg.items.length > 0;
  const visibleTop = Array.isArray(navCfg?.items)
    ? navCfg!.items.filter((i) => i && i.visible !== false)
    : [];

  const topLevel: NavMenuItem[] = (() => {
    const items = visibleTop;
    const limit = navCfg?.limit && navCfg.limit > 0 ? navCfg.limit : null;
    if (!limit || items.length <= limit) return items;
    if (limit === 1) {
      return [
        {
          id: "__more__",
          title: "Menu",
          href: "#",
          visible: true,
          children: items,
        },
      ];
    }
    return [
      ...items.slice(0, Math.max(0, limit - 1)),
      {
        id: "__more__",
        title: "More",
        href: "#",
        visible: true,
        children: items.slice(limit - 1),
      },
    ];
  })();

  /**
   * Top-level label styling — shared by both navs below, and the reason they are
   * written out rather than extracted.
   *
   * The labels are already small (`text-[10px]`, `text-[11px]` from `xl`). What
   * made them read as oversized in the header was **weight and tracking**, not
   * size: an earlier pass moved them from `font-bold`/`tracking-widest` to
   * `font-black` (900) with `tracking-[0.18em]`. Uppercase text has no lowercase
   * ascenders to break up its silhouette, so apparent size is driven almost
   * entirely by stroke weight and letter spacing — 900 weight at 0.18em reads
   * substantially heavier and wider than 700 at 0.10em even at a *smaller* font
   * size, which is how a size reduction ended up looking like a size increase.
   *
   * These labels are also CMS-authored (Admin -> Navigation), and real ones run
   * long — "ALL FOREX BROKERS", "ALL CRYPTO EXCHANGES". Heavy weight multiplied
   * by a long uppercased label is what dominates the row.
   *
   * So: 700, and 0.08em rising to 0.10em at `xl`. If the row still reads too
   * loud, the remaining lever is the content itself — shorter titles in the
   * admin panel — or dropping `uppercase` so labels render as authored.
   */
  const DynamicTopNav = () => {
    if (!topLevel.length) return null;

     const renderChildList = (items: NavMenuItem[], depth = 0) => {
       const visible = (items || []).filter((c) => c && c.visible !== false);
       if (!visible.length) return null;
       return (
         <ul className={cn("list-none space-y-1", depth ? "pl-4" : "")}
           style={depth ? { marginLeft: 6 } : undefined}
         >
           {visible.map((c) => (
             <li key={c.id} className="list-none">
               <ListItem href={c.href} title={c.title} className="text-white">
                 {isExternal(c.href) ? "External link" : ""}
               </ListItem>
               {c.children?.length ? renderChildList(c.children, depth + 1) : null}
             </li>
           ))}
         </ul>
       );
     };

    return (
        <NavigationMenu delayDuration={120}>
         <NavigationMenuList className="gap-0.5 xl:gap-1">
          {topLevel.map((it) => {
            const hasChildren = Array.isArray(it.children) && it.children.filter((c) => c && c.visible !== false).length > 0;
            if (!hasChildren) {
              const external = isExternal(it.href);
              const target = it.newTab || external ? "_blank" : undefined;
              const rel = target === "_blank" ? "noreferrer" : undefined;
              return (
                <NavigationMenuItem key={it.id}>
                  <NavigationMenuLink asChild>
                    {external ? (
                        <a
                          href={it.href}
                          target={target}
                          rel={rel}
                           className="group inline-flex h-10 w-max items-center justify-center whitespace-nowrap rounded-xl bg-transparent px-2.5 py-2 text-[10px] font-bold uppercase tracking-[0.08em] text-white transition-colors hover:bg-white/5 hover:text-primary focus:bg-white/5 focus:text-primary focus:outline-none xl:px-3.5 xl:text-[11px] xl:tracking-[0.1em]"
                        >
                          {it.title}
                        </a>
                    ) : (
                      <Link
                        href={it.href}
                        className="group inline-flex h-10 w-max items-center justify-center whitespace-nowrap rounded-xl bg-transparent px-2.5 py-2 text-[10px] font-bold uppercase tracking-[0.08em] text-white transition-colors hover:bg-white/5 hover:text-primary focus:bg-white/5 focus:text-primary focus:outline-none xl:px-3.5 xl:text-[11px] xl:tracking-[0.1em]"
                      >
                        {it.title}
                      </Link>
                    )}
                  </NavigationMenuLink>
                </NavigationMenuItem>
              );
            }

            return (
              <NavigationMenuItem key={it.id}>
                <NavigationMenuTrigger className="h-10 whitespace-nowrap rounded-xl bg-transparent px-2.5 text-[10px] font-bold uppercase tracking-[0.08em] text-white hover:bg-white/5 hover:text-primary data-[state=open]:bg-white/5 data-[state=open]:text-primary xl:px-3.5 xl:text-[11px] xl:tracking-[0.1em]">
                  {it.title}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                   <div className="grid w-[560px] grid-cols-[240px_1fr]">
                     <div className="p-5">
                       {it.href && it.href !== "#" ? (
                         <NavigationMenuLink asChild>
                           {isExternal(it.href) ? (
                             <a
                               href={it.href}
                               target={it.newTab ? "_blank" : undefined}
                               rel={it.newTab ? "noreferrer" : undefined}
                               className="block rounded-2xl bg-[linear-gradient(180deg,rgba(234,179,8,0.22),rgba(255,255,255,0.04))] p-5"
                             >
                               <div className="text-[11px] font-black uppercase tracking-[0.22em] text-primary">Top Rated</div>
                               <div className="mt-3 text-xl font-black uppercase tracking-tight text-white">{it.title}</div>
                             </a>
                           ) : (
                             <Link
                               href={it.href}
                               className="block rounded-2xl bg-[linear-gradient(180deg,rgba(234,179,8,0.22),rgba(255,255,255,0.04))] p-5"
                             >
                               <div className="text-[11px] font-black uppercase tracking-[0.22em] text-primary">Top Rated</div>
                               <div className="mt-3 text-xl font-black uppercase tracking-tight text-white">{it.title}</div>
                             </Link>
                           )}
                         </NavigationMenuLink>
                       ) : (
                        <div className="rounded-2xl bg-white/5 p-5">
                          {/* No eyebrow line above it any more, so no orphan top margin. */}
                          <div className="text-xl font-black uppercase tracking-tight text-white">{it.title}</div>
                        </div>
                       )}
                     </div>
                     <div className="p-5 border-l border-white/10">
                       {renderChildList(it.children || [], 0)}
                     </div>
                   </div>
                 </NavigationMenuContent>
              </NavigationMenuItem>
            );
          })}
        </NavigationMenuList>
      </NavigationMenu>
    );
  };

  const closeMobileMenu = () => {
    setMobileOpen(false);
    setAccountOpen(false);
  };

  const renderMobileLeafLink = (item: NavMenuItem, depth = 0) => {
    const external = isExternal(item.href);
    const target = item.newTab || external ? "_blank" : undefined;
    const rel = target === "_blank" ? "noreferrer" : undefined;
    const className = cn(
      "group flex items-center justify-between rounded-2xl border border-white/8 bg-white/[0.03] px-4 py-3.5 text-left transition-all",
      "hover:border-primary/30 hover:bg-white/[0.06] hover:text-primary",
      depth > 0 ? "rounded-xl border-white/6 bg-white/[0.02] py-3 text-[15px]" : "text-base",
    );

    const content = (
      <>
        <span className="font-black tracking-tight text-white group-hover:text-primary">{item.title}</span>
        <ArrowRight className="h-4 w-4 text-gray-500 transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
      </>
    );

    if (external) {
      return (
        <a
          href={item.href}
          target={target}
          rel={rel}
          className={className}
          onClick={closeMobileMenu}
        >
          {content}
        </a>
      );
    }

    return (
      <Link href={item.href} className={className} onClick={closeMobileMenu}>
        {content}
      </Link>
    );
  };

  const DynamicMobileNav = () => {
    const renderItems = (items: NavMenuItem[], depth = 0) => {
      const visible = (items || []).filter((c) => c && c.visible !== false);
      if (!visible.length) return null;

      return visible.map((item) => {
        const visibleChildren = (item.children || []).filter((c) => c && c.visible !== false);
        const hasChildren = visibleChildren.length > 0;

        if (!hasChildren) {
          return <div key={item.id}>{renderMobileLeafLink(item, depth)}</div>;
        }

        const directHref = item.href && item.href !== "#" ? item.href : "";

        return (
          <Accordion key={item.id} type="single" collapsible className="rounded-2xl border border-white/8 bg-white/[0.03] px-1">
            <AccordionItem value={item.id} className="border-none">
              <AccordionTrigger className="rounded-2xl px-3 py-3.5 text-left text-base font-black tracking-tight text-white no-underline hover:no-underline">
                <div className="flex min-w-0 flex-col items-start gap-1">
                  <span>{item.title}</span>
                  <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-gray-500">
                    {visibleChildren.length} section{visibleChildren.length > 1 ? "s" : ""}
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-3 pb-3 pt-0">
                <div className="space-y-2.5 border-t border-white/8 pt-3">
                  {directHref ? (
                    <div className="rounded-xl border border-primary/20 bg-primary/10 p-3">
                      <Link
                        href={directHref}
                        className="flex items-center justify-between text-sm font-bold uppercase tracking-[0.1em] text-primary"
                        onClick={closeMobileMenu}
                      >
                        View {item.title}
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                  ) : null}
                  <div className="space-y-2.5">{renderItems(visibleChildren, depth + 1)}</div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        );
      });
    };

    return <div className="space-y-3">{renderItems(topLevel, 0)}</div>;
  };

  const FallbackTopNav = () => (
      <NavigationMenu delayDuration={120}>
       <NavigationMenuList className="gap-0.5 xl:gap-1">
        <NavigationMenuItem>
          <NavigationMenuTrigger className="h-10 whitespace-nowrap rounded-xl bg-transparent px-2.5 text-[10px] font-bold uppercase tracking-[0.08em] text-white hover:bg-white/5 hover:text-primary data-[state=open]:bg-white/5 data-[state=open]:text-primary xl:px-3.5 xl:text-[11px] xl:tracking-[0.1em]">
            Brokers
          </NavigationMenuTrigger>
          <NavigationMenuContent>
            <div className="grid gap-4 p-6 w-[560px] grid-cols-[240px_1fr]">
              <div className="row-span-3">
                <NavigationMenuLink asChild>
                  <a
                    className="flex h-full w-full select-none flex-col justify-end rounded-2xl bg-[linear-gradient(180deg,rgba(234,179,8,0.22),rgba(255,255,255,0.04))] p-6 no-underline outline-none"
                    href="/brokers"
                  >
                    <div className="mb-2 mt-4 text-lg font-black text-white uppercase tracking-tighter">
                      Top Rated Brokers
                    </div>
                  </a>
                </NavigationMenuLink>
              </div>
              <ul className="list-none space-y-1">
                <li className="list-none">
                  <ListItem href="/brokers?type=ecn" title="ECN Brokers" className="text-white" />
                </li>
                <li className="list-none">
                  <ListItem href="/brokers?type=crypto" title="Crypto Trading" className="text-white" />
                </li>
              </ul>
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>

        <NavigationMenuItem>
          <NavigationMenuTrigger className="h-10 whitespace-nowrap rounded-xl bg-transparent px-2.5 text-[10px] font-bold uppercase tracking-[0.08em] text-white hover:bg-white/5 hover:text-primary data-[state=open]:bg-white/5 data-[state=open]:text-primary xl:px-3.5 xl:text-[11px] xl:tracking-[0.1em]">
            Crypto
          </NavigationMenuTrigger>
          <NavigationMenuContent>
            <div className="grid gap-4 p-6 w-[560px] grid-cols-[240px_1fr]">
              <div className="row-span-3">
                <NavigationMenuLink asChild>
                  <a
                    className="flex h-full w-full select-none flex-col justify-end rounded-2xl bg-[linear-gradient(180deg,rgba(234,179,8,0.18),rgba(255,255,255,0.04))] p-6 no-underline outline-none"
                    href="/crypto"
                  >
                    <div className="mb-2 mt-4 text-lg font-black text-white uppercase tracking-tighter">
                      Crypto Exchanges
                    </div>
                  </a>
                </NavigationMenuLink>
              </div>
              <ul className="list-none space-y-1">
                <li className="list-none">
                  <ListItem href="/crypto?type=cex" title="CEX Exchanges" className="text-white" />
                </li>
                <li className="list-none">
                  <ListItem href="/crypto?type=dex" title="DEX Platforms" className="text-white" />
                </li>
              </ul>
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>

        <NavigationMenuItem>
          <NavigationMenuLink asChild>
            <Link
              href="/bonuses"
              className="group inline-flex h-10 w-max items-center justify-center whitespace-nowrap rounded-xl bg-transparent px-2.5 py-2 text-[10px] font-bold uppercase tracking-[0.08em] text-white transition-colors hover:bg-white/5 hover:text-primary focus:bg-white/5 focus:text-primary focus:outline-none disabled:pointer-events-none disabled:opacity-50 xl:px-3.5 xl:text-[11px] xl:tracking-[0.1em]"
            >
              Bonus Offers
            </Link>
          </NavigationMenuLink>
        </NavigationMenuItem>
        <NavigationMenuItem>
          <NavigationMenuLink asChild>
            <Link
              href="/vps"
              className="group inline-flex h-10 w-max items-center justify-center whitespace-nowrap rounded-xl bg-transparent px-2.5 py-2 text-[10px] font-bold uppercase tracking-[0.08em] text-white transition-colors hover:bg-white/5 hover:text-primary focus:bg-white/5 focus:text-primary focus:outline-none disabled:pointer-events-none disabled:opacity-50 xl:px-3.5 xl:text-[11px] xl:tracking-[0.1em]"
            >
              VPS Hosting
            </Link>
          </NavigationMenuLink>
        </NavigationMenuItem>
        <NavigationMenuItem>
          <NavigationMenuLink asChild>
            <Link
              href="/eas"
              className="group inline-flex h-10 w-max items-center justify-center whitespace-nowrap rounded-xl bg-transparent px-2.5 py-2 text-[10px] font-bold uppercase tracking-[0.08em] text-white transition-colors hover:bg-white/5 hover:text-primary focus:bg-white/5 focus:text-primary focus:outline-none disabled:pointer-events-none disabled:opacity-50 xl:px-3.5 xl:text-[11px] xl:tracking-[0.1em]"
            >
              EA Robots
            </Link>
          </NavigationMenuLink>
        </NavigationMenuItem>

        <NavigationMenuItem>
          <NavigationMenuTrigger className="h-10 whitespace-nowrap rounded-xl bg-transparent px-2.5 text-[10px] font-bold uppercase tracking-[0.08em] text-white hover:bg-white/5 hover:text-primary data-[state=open]:bg-white/5 data-[state=open]:text-primary xl:px-3.5 xl:text-[11px] xl:tracking-[0.1em]">
            Markets Insight
          </NavigationMenuTrigger>
          <NavigationMenuContent>
            <div className="p-5 w-[560px]">
              <ul className="list-none grid grid-cols-2 gap-2">
                <li className="list-none">
                  <ListItem href="/intelligence-hub" title="Intelligence Hub" className="text-white">
                    AI-powered insights and real-time tools to stay ahead.
                  </ListItem>
                </li>
                <li className="list-none">
                  <ListItem href="/news?cat=market-news" title="Market News" className="text-white">
                    Latest updates from global financial markets.
                  </ListItem>
                </li>
                <li className="list-none">
                  <ListItem href="/analysis" title="Technical Analysis" className="text-white">
                    Institutional grade chart analysis and setups.
                  </ListItem>
                </li>
                <li className="list-none">
                  <ListItem href="/tools/economic-calendar" title="Economic Calendar" className="text-white">
                    Real-time tracking of high-impact events.
                  </ListItem>
                </li>
                <li className="list-none">
                  <ListItem href="/calculators" title="Trading Calculators" className="text-white">
                    Pip, position size, margin & profit calculators.
                  </ListItem>
                </li>
                <li className="list-none">
                  <ListItem href="/education" title="Education Center" className="text-white">
                    Professional trading guides and strategy tutorials.
                  </ListItem>
                </li>
                <li className="list-none">
                  <ListItem href="/tools/news-digest" title="AI News Digest" className="text-white">
                    AI-powered daily market summaries & insights.
                  </ListItem>
                </li>
                <li className="list-none">
                  <ListItem href="/tools/global-coverage" title="Global Coverage Map" className="text-white">
                    Interactive map of broker & exchange supported regions.
                  </ListItem>
                </li>
              </ul>
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>
  );

  /**
   * The desktop nav, memoised by element identity.
   *
   * `DynamicTopNav` and `FallbackTopNav` are declared in this component, so their
   * identity changes on every render of `Navbar` — see the note on `memo` above. A
   * memoised element is the cheap way to stop that reaching React: while `navCfg` and
   * `hasCustomNav` are unchanged, the *same* element object is handed back, React sees
   * `prev === next` at that position and skips the subtree entirely instead of
   * unmounting and remounting it (which is what closed an open nav panel).
   *
   * `topLevel` is derived from `navCfg` alone, so the closure captured here is always
   * the one that matches the `navCfg` in the dependency list.
   */
  const navMenu = useMemo(
    () => (hasCustomNav ? <DynamicTopNav /> : <FallbackTopNav />),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasCustomNav, navCfg],
  );

  return (
    <header className="sticky top-[var(--market-ticker-h,0px)] z-[100] w-full border-b border-white/5 bg-[#0a101f]/85 shadow-[0_18px_50px_rgba(0,0,0,0.45)] backdrop-blur supports-[backdrop-filter]:bg-[#0a101f]/60">
      <div className="container mx-auto grid h-16 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 px-4 sm:h-20 sm:gap-x-4 md:gap-x-5 lg:gap-x-6 xl:max-w-[1380px] xl:gap-x-8">
        <Link href="/">
          <div className="group flex min-w-0 max-w-[calc(100vw-7.5rem)] items-center space-x-2 cursor-pointer sm:max-w-none sm:space-x-3">
            {showLogo ? (
              <div className="relative flex min-w-0 items-center">
                <div className="absolute inset-0 bg-primary/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                {/*
                  width/height are the intrinsic size of the current logo asset
                  (1080x250 — see the `logoUrl` uploaded in Admin → Settings).
                  The CSS pins the painted height and lets the width follow, so
                  these attributes only supply the *aspect ratio* the renderer
                  reserves before the file arrives — which is what stops the
                  header from reflowing when it lands, and what satisfies the
                  "image elements do not have explicit width and height" audit.

                  If the logo is ever replaced, update these two numbers to the
                  new file's real dimensions; a different ratio would only make
                  the pre-load placeholder the wrong width for one frame.

                  `fetchpriority="high"` because this sits in the LCP viewport
                  region and is the largest image the header paints.
                */}
                <img
                  /*
                   * The uploaded logo is 1080x250 and is drawn at most 206 CSS
                   * px wide, so ask the server for a 512px derivative instead of
                   * shipping the 24 KB original. `width`/`height` below stay the
                   * original's intrinsic size — they only reserve space.
                   */
                  src={sizedUploadUrl(logoUrl, SITE_LOGO_IMAGE_WIDTH)}
                  alt={siteName}
                  width={1080}
                  height={250}
                  className="relative block h-8 w-auto max-w-[124px] object-contain sm:h-9 sm:max-w-[158px] lg:h-10 lg:max-w-[186px] xl:h-11 xl:max-w-[206px]"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  onError={() => setLogoFailedUrl(logoUrl)}
                />
              </div>
            ) : (
              <>
                <div className="relative">
                  <div className="absolute inset-0 bg-primary/30 rounded-xl blur-md opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  <div className="relative flex h-10 w-10 items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br from-primary via-yellow-400 to-primary shadow-lg transition-transform group-hover:scale-110 sm:h-12 sm:w-12">
                    {/*
                      The site's own initials, not a hardcoded mark. `siteInitials`
                      turns "BullBuzz" into "BB"; with no name at all it returns "BB"
                      as well, so this badge can never advertise another brand.
                    */}
                    <span className="text-lg font-black text-black sm:text-xl">
                      {siteInitials(siteName)}
                    </span>
                  </div>
                </div>
                <div className="flex min-w-0 flex-col">
                  <div className="flex min-w-0 items-baseline gap-1.5 sm:gap-2">
                    <span className="truncate font-heading text-lg font-black leading-none tracking-tight text-white sm:text-2xl">
                      {brandA}
                    </span>
                    {brandB ? (
                      <span className="shrink-0 font-heading text-lg font-black leading-none text-primary sm:text-2xl">{brandB}</span>
                    ) : null}
                  </div>
                  <span className="mt-0.5 hidden truncate text-[8px] font-bold uppercase tracking-[0.32em] text-gray-500 sm:block">{tagline}</span>
                </div>
              </>
            )}
          </div>
        </Link>

        {/* Desktop Nav — only once there is room for it; tablets use the sheet. */}
        <div className="hidden lg:flex min-w-0 items-center justify-center pl-1 xl:pl-2">
          <div className="min-w-0 max-w-full overflow-visible">
            {navMenu}
          </div>
        </div>

        {/* The controls sit above the nav dropdown panel and are visually separated from
            the menu row, so an open menu can never look like it swallows the search box. */}
        <div className="relative z-10 ml-auto flex shrink-0 items-center justify-end gap-1.5 sm:gap-2 md:gap-2.5 xl:gap-3">
          {/* Short hairline instead of a full-height column border. */}
          <div className="mr-1 hidden h-6 w-px shrink-0 bg-white/10 lg:block" aria-hidden="true" />
          <div className="hidden xl:block">
            <GlobalSearch />
          </div>
          <div className="hidden md:flex items-center gap-3 lg:gap-4 xl:gap-6">
            {isAuthenticated ? (
              <DropdownMenu modal={false}>
                {/*
                  Avatar only. The name and the chevron together took roughly 180px of the
                  navbar's right-hand column on every desktop — the widest thing in a row
                  that also has to hold the search and the language switcher — for a name
                  the open menu already shows. The control is still a full 40px tap target
                  and keeps a visible ring, but its content is now just the round picture.
                  (`asChild` means the trigger's only child must be the button itself, so
                  this note lives here rather than inside it.)
                */}
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label={user?.firstName ? `Account menu — ${user.firstName}` : "Account menu"}
                    className="grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-full border border-white/10 bg-white/5 transition-colors hover:border-primary/40 hover:bg-white/10"
                  >
                    {/*
                      Either the picture or the glyph — never both. Layering the glyph
                      underneath was tried and measured: a transparent avatar PNG lets it
                      show through, and a failed <img> with `alt=""` still paints Chrome's
                      broken-image glyph on top of it. Removing the picture on error gives
                      the clean glyph either way.
                    */}
                    {showAvatar ? (
                      <img
                        src={avatarUrl}
                        alt=""
                        onError={() => setAvatarFailedUrl(avatarUrl)}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <UserIcon className="w-4 h-4 text-gray-300" />
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 bg-[#0a101f] border-white/10 text-white" align="end">
                  <DropdownMenuLabel className="text-gray-300">My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem onClick={() => setLocation("/dashboard")} className="cursor-pointer focus:bg-white/5">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setLocation("/dashboard/profile")} className="cursor-pointer focus:bg-white/5">
                    <UserIcon className="w-4 h-4 mr-2" /> Edit Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setLocation("/dashboard/reviews")} className="cursor-pointer focus:bg-white/5">
                    <Star className="w-4 h-4 mr-2" /> My Reviews
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setLocation("/dashboard/complaints")} className="cursor-pointer focus:bg-white/5">
                    <AlertTriangle className="w-4 h-4 mr-2" /> My Complaints
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setLocation("/dashboard/messages")} className="cursor-pointer focus:bg-white/5">
                    <MessageSquare className="w-4 h-4 mr-2" /> My Messages
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem
                    onClick={async () => {
                      await logout();
                      setLocation("/");
                    }}
                    disabled={isLoggingOut}
                    className="cursor-pointer focus:bg-white/5"
                  >
                    <LogOut className="w-4 h-4 mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link href="/auth">
                  <Button className="h-10 whitespace-nowrap rounded-xl bg-primary px-4 text-[10px] font-black uppercase tracking-[0.18em] text-black shadow-lg shadow-primary/20 transition-all hover:bg-white xl:px-6 xl:text-[11px] xl:tracking-[0.2em]">
                    Login / Register
                  </Button>
                </Link>
              </>
            )}
            <div className="hidden sm:block">
              <LanguageSwitcher />
            </div>
          </div>
          
          {/* Mobile Menu */}
            <Sheet
              open={mobileOpen}
              onOpenChange={(open) => {
                setMobileOpen(open);
                // Closing via the overlay or the X does not go through closeMobileMenu.
                if (!open) setAccountOpen(false);
              }}
            >
              <SheetTrigger asChild>
                {/*
                  `aria-label` is required: the button's only child is an icon, so
                  it has no accessible name and a screen reader announces it as
                  just "button" (PageSpeed's button-name audit flagged exactly
                  this node). The icon is decorative once the button is named.
                */}
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Open menu"
                  className="h-10 w-10 shrink-0 rounded-xl text-white hover:bg-white/5 hover:text-primary lg:hidden"
                >
                  <Menu className="h-5 w-5" aria-hidden="true" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[88vw] max-w-sm overflow-y-auto border-l border-white/10 bg-[#0a101f] !pt-16">
                <div className="space-y-6">

                  {hasCustomNav ? (
                    <DynamicMobileNav />
                  ) : (
                    <div className="space-y-3">
                      {[
                        { href: "/brokers", title: "Brokers" },
                        { href: "/crypto", title: "Crypto" },
                        { href: "/bonuses", title: "Bonus Offers" },
                        { href: "/vps", title: "VPS Hosting" },
                        { href: "/eas", title: "EA Robots" },
                        { href: "/news", title: "News" },
                        { href: "/analysis", title: "Analysis" },
                        { href: "/education", title: "Education" },
                      ].map((item) => (
                        <div key={item.href}>{renderMobileLeafLink({ ...item, id: item.href }, 0)}</div>
                      ))}
                    </div>
                  )}

                  {/* The drawer used to list five account links plus a Logout button as a
                      flat block, which dominated the panel on a phone. It is now a single
                      profile row that expands to Dashboard + Logout; the other
                      destinations are one tap away on the dashboard index itself. */}
                  <div className="border-t border-white/10 pt-5">
                    <div className="mb-3 text-[10px] font-black uppercase tracking-[0.28em] text-gray-500">Account</div>
                    {isAuthenticated ? (
                      <div>
                        <button
                          type="button"
                          onClick={() => setAccountOpen((s) => !s)}
                          aria-expanded={accountOpen}
                          aria-controls="mobile-account-panel"
                          className="flex w-full items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-3 text-left transition-colors hover:bg-white/5"
                        >
                          <div className="w-10 h-10 shrink-0 overflow-hidden rounded-2xl border border-white/10 bg-[#070b14] grid place-items-center">
                            {user?.profileImageUrl ? (
                              <img src={user.profileImageUrl} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <UserIcon className="w-5 h-5 text-gray-300" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="truncate font-black text-white">
                              {user?.firstName || user?.email || "Account"}
                            </div>
                            <div className="truncate text-xs text-gray-500">{user?.email || ""}</div>
                          </div>
                          <ChevronDown
                            className={cn(
                              "h-5 w-5 shrink-0 text-gray-400 transition-transform duration-300 ease-out",
                              accountOpen && "rotate-180",
                            )}
                          />
                        </button>

                        {/* Same 0fr → 1fr reveal used by the dashboard sidebar; the
                            overflow-hidden child is what lets the row reach zero height. */}
                        <div
                          id="mobile-account-panel"
                          className={cn(
                            "grid transition-[grid-template-rows] duration-300 ease-out",
                            accountOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
                          )}
                        >
                          <div className="overflow-hidden">
                            <div className="mt-3 space-y-3">
                              {renderMobileLeafLink(
                                { href: "/dashboard", title: "Dashboard", id: "/dashboard" },
                                0,
                              )}
                              <Button
                                className="h-12 w-full rounded-2xl bg-primary font-black text-primary-foreground"
                                onClick={async () => {
                                  await logout();
                                  closeMobileMenu();
                                  setLocation("/");
                                }}
                                disabled={isLoggingOut}
                              >
                                Logout
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <Link href="/auth" onClick={closeMobileMenu} className="block">
                        <Button className="h-12 w-full rounded-2xl bg-primary font-black text-primary-foreground shadow-lg shadow-primary/20">
                          Login / Register
                        </Button>
                      </Link>
                     )}
                   </div>

                   <div className="border-t border-white/10 pt-5">
                     <LanguageSwitcher variant="row" />
                   </div>
                 </div>
               </SheetContent>
            </Sheet>
        </div>
      </div>
    </header>
  );
}

const ListItem = ({ className, title, children, href, ...props }: any) => {
  return (
    <NavigationMenuLink asChild>
      <Link
        href={href}
        className={cn(
          "block select-none rounded-xl px-3 py-3 no-underline outline-none transition-colors",
          "hover:bg-white/5 focus:bg-white/5",
          "cursor-pointer",
          className,
        )}
        {...props}
      >
        <div className="text-[13px] font-black text-white leading-none">{title}</div>
        {children ? <div className="mt-1 text-[12px] leading-snug text-slate-400 line-clamp-2">{children}</div> : null}
      </Link>
    </NavigationMenuLink>
  );
};

/**
 * `memo` on the export, not on the declaration, so the internal name stays
 * `NavbarInner` and the reason for the wrapper is documented on it. The header takes
 * no props, so the comparison is always equal and the component re-renders only for
 * its own reasons — its state, its queries, or a context change. See the note above
 * `NavbarInner` for what this fixes.
 */
export const Navbar = memo(NavbarInner);
